import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Printer, Pencil, Trash2, Check, X, CreditCard, ArrowRightLeft } from "lucide-react";
import { useApi, errMsg, money, fmtDate } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { DocEditor } from "@/components/portal/DocEditor";
import { PageHeader, Empty, StatusBadge } from "@/components/portal/StatusBadge";

const Btn = ({ onClick, icon: Icon, children, testId, primary }) => (
  <button onClick={onClick} data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${primary ? "bg-[#F2B91C] text-[#0E1F3F] hover:bg-[#D9A30F]" : "border border-[#E6E2DA] bg-white hover:border-[#0E1F3F]"}`}>
    <Icon size={13} />{children}
  </button>
);

export default function DocsPage({ kind }) {
  const api = useApi();
  const me = useMe();
  const isAdmin = me.role === "admin";
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null);
  const label = kind === "estimates" ? "Estimates" : "Invoices";

  useEffect(() => {
    setDocs(null);
    api("get", `/${kind}`).then(setDocs);
  }, [api, kind]);

  const replace = (d) => setDocs(docs.map((x) => (x.id === d.id ? d : x)));
  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const respond = (d, a) => act(async () => replace(await api("post", `/estimates/${d.id}/respond/${a}`)), a === "approve" ? "Estimate approved — thank you!" : "Estimate declined");
  const remove = (d) => window.confirm(`Delete ${d.number}?`) && act(async () => { await api("delete", `/${kind}/${d.id}`); setDocs(docs.filter((x) => x.id !== d.id)); }, "Deleted");
  const convert = (d) => act(async () => { await api("post", `/estimates/${d.id}/convert`); }, "Draft invoice created from estimate");
  const pay = (d) => act(async () => {
    const r = await api("post", `/invoices/${d.id}/pay`, { origin_url: window.location.origin });
    window.location.href = r.checkout_url;
  });

  const Actions = ({ d }) => (
    <div className="flex flex-wrap justify-end gap-1.5">
      <Link to={`/portal/print/${kind}/${d.id}`} data-testid={`view-${d.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#E6E2DA] bg-white px-3 py-1.5 text-xs font-semibold hover:border-[#0E1F3F]"><Printer size={13} />View / PDF</Link>
      {isAdmin && <Btn icon={Pencil} onClick={() => setEditing(d)} testId={`edit-${d.id}`}>Edit</Btn>}
      {isAdmin && kind === "estimates" && d.status === "approved" && !d.invoice_id && <Btn icon={ArrowRightLeft} onClick={() => convert(d)} testId={`convert-${d.id}`}>To invoice</Btn>}
      {isAdmin && <Btn icon={Trash2} onClick={() => remove(d)} testId={`delete-${d.id}`}>Delete</Btn>}
      {!isAdmin && kind === "estimates" && d.status === "sent" && <><Btn primary icon={Check} onClick={() => respond(d, "approve")} testId={`approve-${d.id}`}>Approve</Btn><Btn icon={X} onClick={() => respond(d, "decline")} testId={`decline-${d.id}`}>Decline</Btn></>}
      {!isAdmin && kind === "invoices" && ["sent", "overdue"].includes(d.status) && <Btn primary icon={CreditCard} onClick={() => pay(d)} testId="invoice-pay-button">Pay {money(d.total)}</Btn>}
    </div>
  );

  return (
    <div data-testid={`${kind}-page`}>
      <PageHeader eyebrow={isAdmin ? "Billing" : "Your documents"} title={label}>
        {isAdmin && <button onClick={() => setEditing({})} data-testid={`new-${kind}-btn`} className="inline-flex items-center gap-2 rounded-full bg-[#F2B91C] px-5 py-2.5 text-sm font-semibold text-[#0E1F3F] hover:bg-[#D9A30F]"><Plus size={16} />New</button>}
      </PageHeader>
      {docs && !docs.length && <Empty text={`No ${label.toLowerCase()} yet.`} />}
      <div className="space-y-3">
        {docs?.map((d) => (
          <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex flex-col gap-3 rounded-xl border border-[#E6E2DA] bg-white p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3"><span className="font-mono-num text-sm font-semibold">{d.number}</span><StatusBadge status={d.status} testId={`doc-status-${d.id}`} /></div>
              <p className="mt-1 font-semibold">{d.title || d.project_name || "Untitled"}</p>
              <p className="text-xs text-[#5C5850]">{d.customer_name || d.customer_email} · {fmtDate(d.created_at)}{d.due_date ? ` · Due ${fmtDate(d.due_date)}` : ""}</p>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <p className="font-mono-num text-xl font-semibold" data-testid={`doc-total-${d.id}`}>{money(d.total)}</p>
              <Actions d={d} />
            </div>
          </div>
        ))}
      </div>
      <DocEditor kind={kind} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} doc={editing?.id ? editing : null}
        onSaved={(d) => (docs.some((x) => x.id === d.id) ? replace(d) : setDocs([d, ...docs]))} />
    </div>
  );
}
